import { Controller } from '@hotwired/stimulus';

// Délai de secours avant `hidden` à la disparition : la transition de la barre dure
// `duration-150` (DS8) ; on ne compte jamais sur `transitionend`
// (`slide_over_controller.js:191`), qui ne part pas si la transition est coupée.
const HIDE_FALLBACK_MS = 200;

/**
 * sticky-reveal — révèle une barre quand une SENTINELLE sort de la zone de défilement.
 *
 * Contrôleur générique (D30) : rien de propre au CR. La sentinelle vit hors de
 * l'élément du contrôleur (typiquement l'en-tête d'un long document), d'où une valeur
 * d'id plutôt qu'une cible.
 *
 * Targets:
 *   bar — le nœud basculé. Rendu MASQUÉ par le serveur : `hidden` + `inert` + classes
 *         concealed. Masqué, il sort de l'arbre d'accessibilité et du parcours clavier
 *         (précédent `disclosure_controller.js:55`).
 *
 * Values:
 *   sentinelId   — id de l'élément observé, résolu par `document.getElementById`.
 *   rootSelector — racine de l'IntersectionObserver (défaut `main`, la zone qui défile).
 *
 * Classes (getters PLURIELS, gardés par `has…Class`) :
 *   revealed  — état visible de la transition (ex. opacité pleine, sans translation).
 *   concealed — état masqué de la transition, posé par le serveur au rendu.
 *
 * Séquence (spec 2026-10-07 § 5.2, reprise de `slide_over_controller.js:137-190`) :
 *   - apparition : retrait de `hidden` et `inert`, puis concealed → revealed au
 *     prochain `requestAnimationFrame` pour que la transition parte ;
 *   - disparition : `inert` posé TOUT DE SUITE, revealed → concealed, puis `hidden`
 *     après HIDE_FALLBACK_MS ; une réapparition entre-temps annule le masquage ;
 *   - focus dans la barre : pas de masquage, il est différé au `focusout` qui sort
 *     de la barre (et n'a lieu que si la sentinelle est toujours visible) ;
 *   - `prefers-reduced-motion: reduce` : aucun délai, `hidden` dans le même tour ;
 *   - sans `IntersectionObserver` ou sans sentinelle : la barre reste masquée.
 *
 * N'écrit que les attributs `hidden` / `inert` et les classes des valeurs : jamais de
 * style en ligne, jamais de couleur (le lint ERB ne voit pas les .js).
 *
 * @example
 * <div data-controller="sticky-reveal"
 *      data-sticky-reveal-sentinel-id-value="document-header"
 *      data-sticky-reveal-revealed-class="opacity-100 translate-y-0"
 *      data-sticky-reveal-concealed-class="opacity-0 -translate-y-2">
 *   <div data-sticky-reveal-target="bar" role="region" aria-label="Actions"
 *        class="opacity-0 -translate-y-2 transition duration-150 ease-out
 *               motion-reduce:transition-none"
 *        hidden inert>
 *     …
 *   </div>
 * </div>
 */
export default class extends Controller {
  static targets = ['bar'];
  static values = {
    sentinelId: String,
    rootSelector: { type: String, default: 'main' },
  };
  static classes = ['revealed', 'concealed'];

  connect() {
    this.shown = false;
    this.sentinelVisible = true;
    this.revealFrame = null;
    this.hideTimeout = null;
    this.observer = null;

    if (typeof IntersectionObserver === 'undefined' || !this.hasBarTarget) return;
    const sentinel = this.sentinelIdValue ? document.getElementById(this.sentinelIdValue) : null;
    if (!sentinel) return;

    const root = this.rootSelectorValue ? document.querySelector(this.rootSelectorValue) : null;
    this.observer = new IntersectionObserver(this.onIntersect, { root });
    this.observer.observe(sentinel);
    this.barTarget.addEventListener('focusout', this.onFocusout);
  }

  disconnect() {
    this.observer?.disconnect();
    this.observer = null;
    this.#cancelReveal();
    this.#cancelHide();
    if (this.hasBarTarget) this.barTarget.removeEventListener('focusout', this.onFocusout);
  }

  /** @param {IntersectionObserverEntry[]} entries */
  onIntersect = (entries) => {
    const entry = entries.at(-1);
    if (!entry) return;
    this.sentinelVisible = entry.isIntersecting;
    if (this.sentinelVisible) this.#conceal();
    else this.#reveal();
  };

  /**
   * Le focus quitte la barre : un masquage différé par le focus a lieu maintenant,
   * si la sentinelle est encore visible.
   *
   * @param {FocusEvent} event
   */
  onFocusout = (event) => {
    if (event.relatedTarget && this.barTarget.contains(event.relatedTarget)) return;
    if (this.sentinelVisible) this.#conceal();
  };

  #reveal() {
    this.#cancelHide();
    if (this.shown) return;
    this.shown = true;

    const bar = this.barTarget;
    bar.hidden = false;
    bar.inert = false;

    if (this.#reducedMotion()) {
      this.#applyRevealed();
      return;
    }
    // Cadre suivant : l'état concealed est peint avant la bascule, la transition part.
    this.revealFrame = requestAnimationFrame(() => {
      this.revealFrame = null;
      this.#applyRevealed();
    });
  }

  #conceal() {
    if (!this.shown) return;
    if (this.#focusInside()) return; // différé au focusout
    this.shown = false;
    this.#cancelReveal();

    const bar = this.barTarget;
    bar.inert = true;
    if (this.hasRevealedClass) bar.classList.remove(...this.revealedClasses);
    if (this.hasConcealedClass) bar.classList.add(...this.concealedClasses);

    if (this.#reducedMotion()) {
      bar.hidden = true;
      return;
    }
    this.#cancelHide();
    this.hideTimeout = setTimeout(() => {
      this.hideTimeout = null;
      bar.hidden = true;
    }, HIDE_FALLBACK_MS);
  }

  #applyRevealed() {
    const bar = this.barTarget;
    if (this.hasConcealedClass) bar.classList.remove(...this.concealedClasses);
    if (this.hasRevealedClass) bar.classList.add(...this.revealedClasses);
  }

  #focusInside() {
    const active = document.activeElement;
    return Boolean(active) && this.barTarget.contains(active);
  }

  #reducedMotion() {
    return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
  }

  #cancelReveal() {
    if (this.revealFrame !== null) {
      cancelAnimationFrame(this.revealFrame);
      this.revealFrame = null;
    }
  }

  #cancelHide() {
    if (this.hideTimeout !== null) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }
}
